import {
  createDatabaseBlockDataSource,
  DatabaseBlockDataSource,
  isDatabaseStorageModelReady,
  observeDatabaseStorageModelReady,
} from '@blocksuite/affine/blocks/database';
import type { DatabaseBlockModel } from '@blocksuite/affine-model';
import type { Store } from '@blocksuite/store';
import { effect } from '@preact/signals-core';

export type RelationRowsWindowRequest = {
  offset?: number;
  limit: number;
  searchText?: string;
  signal?: AbortSignal;
};

export type RelationRowInfo = {
  id: string;
  display: string;
};

export type RelationRowsWindow = {
  complete?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  hasMore: boolean;
  rows: RelationRowInfo[];
};

const dataSourcesByModel = new WeakMap<
  DatabaseBlockModel,
  DatabaseBlockDataSource
>();

/** Missing: a part of the target rows is not on the server; it never loads. */
export type RelationRowDataLoadState = 'loading' | 'ready' | 'missing';

type RelationRowDataSource = DatabaseBlockDataSource & {
  ensureRelationRowDataLoaded?: () => void;
  getRelationRowDataLoadState?: () => RelationRowDataLoadState;
};

export function formatRelationDisplayValue(value: unknown): string {
  if (value === null || value === undefined) return '(empty)';
  if (typeof value === 'string') return value || '(empty)';
  if (Array.isArray(value)) return value.join(', ') || '(empty)';
  if (typeof value === 'object' && 'toString' in value) {
    return value.toString() || '(empty)';
  }
  return String(value);
}

export type DatabaseStorageState = 'missing' | 'loading' | 'ready';

export function findDatabaseBlockModel(
  store: Store,
  databaseId: string
): DatabaseBlockModel | undefined {
  const databases = store.getBlocksByFlavour('affine:database');
  const targetDb = databases.find(db => db.id === databaseId);

  if (!targetDb) {
    return;
  }

  return ((targetDb as unknown as { model?: DatabaseBlockModel }).model ??
    targetDb) as DatabaseBlockModel;
}

export function getDatabaseStorageState(
  store: Store,
  databaseId: string
): DatabaseStorageState {
  const model = findDatabaseBlockModel(store, databaseId);
  if (!model) {
    return 'missing';
  }
  return isDatabaseStorageModelReady(model) ? 'ready' : 'loading';
}

export function getDatabaseDataSource(
  store: Store,
  databaseId: string
): DatabaseBlockDataSource | undefined {
  const model = findDatabaseBlockModel(store, databaseId);

  // Constructing a DataSource before the sharded root doc is synced throws
  // inside resolveDatabaseStorageModel — callers must treat undefined as
  // "not available (yet)" and re-check via observeDatabaseStorageModelReady.
  if (!model || !isDatabaseStorageModelReady(model)) {
    return;
  }

  let dataSource = dataSourcesByModel.get(model);
  if (!dataSource) {
    dataSource = createDatabaseBlockDataSource(model);
    dataSourcesByModel.set(model, dataSource);
  }
  return dataSource;
}

export function ensureRelationRowDataLoaded(
  dataSource: DatabaseBlockDataSource
): void {
  (dataSource as RelationRowDataSource).ensureRelationRowDataLoaded?.();
}

export function getRelationRowDataLoadState(
  dataSource: DatabaseBlockDataSource
): RelationRowDataLoadState {
  return (
    (dataSource as RelationRowDataSource).getRelationRowDataLoadState?.() ??
    'ready'
  );
}

export function getDatabaseRowsWindow(
  dataSource: DatabaseBlockDataSource,
  displayFieldId: string,
  request: RelationRowsWindowRequest
): RelationRowsWindow {
  const projection = request.searchText?.trim()
    ? dataSource.projectRows({
        scope: 'filtered-result',
        searchText: request.searchText.trim(),
        requestedPropertyIds: [displayFieldId],
        window: { offset: request.offset, limit: request.limit },
      })
    : null;
  const rowWindow = projection
    ? {
        offset: projection.window.offset,
        limit: projection.window.limit,
        totalCount: projection.totalCount,
        hasMore:
          projection.window.offset + projection.rowIds.length <
          projection.totalCount,
        rowIds: projection.rowIds,
      }
    : dataSource.getRowsWindow(request);
  return {
    complete:
      projection?.complete ??
      rowWindow.rowIds.length ===
        Math.max(
          0,
          Math.min(rowWindow.limit, rowWindow.totalCount - rowWindow.offset)
        ),
    offset: rowWindow.offset,
    limit: rowWindow.limit,
    totalCount: rowWindow.totalCount,
    hasMore: rowWindow.hasMore,
    rows: rowWindow.rowIds.map(rowId => ({
      id: rowId,
      display: formatRelationDisplayValue(
        dataSource.cellValueGet(rowId, displayFieldId)
      ),
    })),
  };
}

export function rowExistsInDataSource(
  dataSource: DatabaseBlockDataSource,
  rowId: string
): boolean {
  return dataSource.rowExists(rowId);
}

export function getRowDisplayValueFromDataSource(
  dataSource: DatabaseBlockDataSource,
  rowId: string,
  displayFieldId: string
): string | undefined {
  if (!rowExistsInDataSource(dataSource, rowId)) {
    return;
  }
  return formatRelationDisplayValue(
    dataSource.cellValueGet(rowId, displayFieldId)
  );
}

/** Wait on the existing storage/shard signals; never poll or publish partial search results. */
export function waitForDatabaseRowsWindow(
  store: Store,
  databaseId: string,
  displayFieldId: string,
  request: RelationRowsWindowRequest
): Promise<RelationRowsWindow> {
  const signal = request.signal ?? AbortSignal.timeout(15_000);
  return new Promise((resolve, reject) => {
    let settled = false;
    const cleanups: Array<() => void> = [];
    const finish = (result?: RelationRowsWindow, error?: unknown) => {
      if (settled) return;
      settled = true;
      cleanups.splice(0).forEach(cleanup => cleanup());
      if (error !== undefined) reject(error);
      else resolve(result!);
    };
    // Observers can invoke their callback synchronously while being installed.
    const retain = (cleanup: () => void) => {
      if (settled) cleanup();
      else cleanups.push(cleanup);
    };
    const abort = () =>
      finish(
        undefined,
        signal.reason ?? new Error('Relation rows request aborted')
      );
    if (signal.aborted) {
      abort();
      return;
    }
    signal.addEventListener('abort', abort, { once: true });
    retain(() => signal.removeEventListener('abort', abort));
    const model = findDatabaseBlockModel(store, databaseId);
    if (!model) {
      finish(undefined, new Error('Relation target database unavailable'));
      return;
    }
    let observing = false;
    const observeRows = () => {
      if (settled || observing) return;
      const source = getDatabaseDataSource(store, databaseId);
      if (!source) return;
      observing = true;
      retain(
        effect(() => {
          try {
            const result = getDatabaseRowsWindow(
              source,
              displayFieldId,
              request
            );
            if (result.complete) finish(result);
          } catch (error) {
            finish(undefined, error);
          }
        })
      );
    };
    try {
      observeRows();
      if (!observing && !settled)
        retain(observeDatabaseStorageModelReady(model, observeRows));
    } catch (error) {
      finish(undefined, error);
    }
  });
}
